import { redirectFBReferrals } from "./src/utils/functions";
import {
  ANALYTICS_CONSENT_ACCEPTED,
  ANALYTICS_CONSENT_DECLINED,
  hasAnalyticsConsent,
  setAnalyticsConsent,
} from "./src/utils/analyticsConsent";
import themeConfig from "./src/config/theme";

// custom css
import "typeface-poppins";

// normalize CSS across browsers
import "./src/css/normalize.css";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// custom CSS styles
import "./src/css/style.css";
import "./src/css/@wordpress/block-library/build-style/style.css";
import "./src/css/@wordpress/block-library/build-style/theme.css";
import "./src/styles/grid.css";
import "./src/styles/btn.css";
import "./src/styles/style.css";

const GTM_SCRIPT_ID = "ordersco-gtm-script";
const GTM_ROUTE_CHANGE_EVENT = "gatsby-route-change";
const GTM_FALLBACK_ORIGIN = "https://www.googletagmanager.com";
const GTM_LOAD_DELAY_MS = 3500;

const resetDataLayer = () => {
  if (typeof window === "undefined") {
    return [];
  }

  window.dataLayer = [];
  return window.dataLayer;
};

const normalizeOrigin = (value, fallback = GTM_FALLBACK_ORIGIN) => {
  if (!value) {
    return fallback;
  }

  const trimmedValue = value.trim().replace(/^['"]|['"]$/g, "");

  if (!trimmedValue) {
    return fallback;
  }

  return /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue.replace(/\/$/, "")
    : `https://${trimmedValue.replace(/\/$/, "")}`;
};

const getGtmConfig = () => {
  const id = process.env.GATSBY_GTM_ID;

  if (!id) {
    return null;
  }

  return {
    id,
    auth: process.env.GATSBY_GTM_AUTH,
    preview: process.env.GATSBY_GTM_PREVIEW,
    origin: normalizeOrigin(process.env.GATSBY_GTM_SELF_HOSTED_ORIGIN),
  };
};

const ensureDataLayer = () => {
  if (typeof window === "undefined") {
    return [];
  }

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const buildGtmUrl = ({ id, auth, preview, origin }) => {
  const url = new URL(`${origin}/gtm.js`);
  url.searchParams.set("id", id);

  if (auth) {
    url.searchParams.set("gtm_auth", auth);
  }

  if (preview) {
    url.searchParams.set("gtm_preview", preview);
    url.searchParams.set("gtm_cookies_win", "x");
  }

  return url.toString();
};

const loadGoogleTagManager = ({ resetQueuedEvents = false } = {}) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const config = getGtmConfig();
  if (
    !config ||
    window.__orderscoGtmLoaded ||
    window.__orderscoGtmLoading
  ) {
    return;
  }

  if (document.getElementById(GTM_SCRIPT_ID)) {
    window.__orderscoGtmLoaded = true;
    return;
  }

  window.__orderscoGtmLoading = true;
  const dataLayer = resetQueuedEvents ? resetDataLayer() : ensureDataLayer();
  dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = buildGtmUrl(config);
  script.onload = () => {
    window.__orderscoGtmLoaded = true;
    window.__orderscoGtmLoading = false;
  };
  script.onerror = () => {
    window.__orderscoGtmLoading = false;
  };

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

const scheduleGoogleTagManagerLoad = () => {
  if (
    typeof window === "undefined" ||
    !hasAnalyticsConsent() ||
    window.__orderscoGtmScheduled
  ) {
    return;
  }

  window.__orderscoGtmScheduled = true;

  let didTrigger = false;
  let timeoutId;
  const interactionEvents = ["pointerdown", "keydown", "touchstart", "scroll"];

  function triggerLoad() {
    if (didTrigger) {
      return;
    }

    didTrigger = true;
    interactionEvents.forEach((eventName) => {
      window.removeEventListener(eventName, triggerLoad);
    });
    window.removeEventListener("load", triggerLoad);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => {
        loadGoogleTagManager();
      }, { timeout: 2000 });
      return;
    }

    window.setTimeout(() => {
      loadGoogleTagManager();
    }, 0);
  }

  if (document.readyState === "complete") {
    triggerLoad();
    return;
  }

  window.addEventListener("load", triggerLoad, { once: true });

  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, triggerLoad, {
      once: true,
      passive: true,
    });
  });

  timeoutId = window.setTimeout(triggerLoad, GTM_LOAD_DELAY_MS);
};

const initializeAnalyticsConsent = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.__orderscoGrantAnalyticsConsent = () => {
    setAnalyticsConsent(ANALYTICS_CONSENT_ACCEPTED);
    loadGoogleTagManager({ resetQueuedEvents: true });
  };

  window.__orderscoDenyAnalyticsConsent = () => {
    setAnalyticsConsent(ANALYTICS_CONSENT_DECLINED);
    resetDataLayer();
  };
};

export const onClientEntry = () => {
  themeConfig.applyTheme(themeConfig.defaultThemeName);
  redirectFBReferrals();
  ensureDataLayer();
  initializeAnalyticsConsent();
  loadGoogleTagManager();

  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
};

export const onInitialClientRender = () => {
  if (typeof window !== "undefined") {
    window.scrollTo(0, 0);
  }
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window === "undefined" || !location) {
    return;
  }

  ensureDataLayer().push({
    event: GTM_ROUTE_CHANGE_EVENT,
    pagePath: `${location.pathname}${location.search}${location.hash}`,
    pageLocation: window.location.href,
    previousPagePath: prevLocation
      ? `${prevLocation.pathname}${prevLocation.search}${prevLocation.hash}`
      : undefined,
  });
};
